import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import Box from "@mui/material/Box";
import {alpha} from "@mui/material/styles";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import type {CSSProperties, ForwardedRef, ReactElement, Ref} from "react";
import {forwardRef} from "react";
import {useTranslation} from "react-i18next";
import {variableChipSx} from "../../../variables/variableChipStyles";
import {firstMatchRange} from "../../model/search";
import HighlightedText from "../HighlightedText";
import type {PickerItem} from "../pickerItem";

export interface PickerRowProps<I extends PickerItem = PickerItem> {
    item: I;
    /** The row's index among rows, which is what the keyboard moves through. */
    index: number;
    /** The row's index among entries, which is what the virtualizer measures. */
    entryIndex: number;
    id: string;
    query: string;
    selected: boolean;
    /** This row's info card is open. */
    infoOpen: boolean;
    /** The virtualizer's absolute position for the row. */
    style?: CSSProperties;
    onPointerSelect: (index: number, x: number, y: number) => void;
    onSelect: (item: I) => void;
    onOpenInfo: (anchor: HTMLElement, item: I, pinned: boolean) => void;
    onCloseInfoSoon: () => void;
}

function PickerRowInner<I extends PickerItem>(
    {
        item,
        index,
        entryIndex,
        id,
        query,
        selected,
        infoOpen,
        style,
        onPointerSelect,
        onSelect,
        onOpenInfo,
        onCloseInfoSoon,
    }: PickerRowProps<I>,
    ref: ForwardedRef<HTMLLIElement>,
) {
    const {t} = useTranslation();

    /*
        The label shows only when it is why the row matched. A key search looks exactly as before;
        a search by meaning ("arrival") explains why `checkInDate` is in the list.
     */
    const showLabel = !!query.trim() && firstMatchRange(item.label, query) !== null;

    return (
        <Stack
            ref={ref}
            component="li"
            id={id}
            role="option"
            aria-selected={selected}
            data-index={entryIndex}
            direction="row"
            alignItems="center"
            gap={1.5}
            onMouseMove={event => onPointerSelect(index, event.clientX, event.clientY)}
            onMouseDown={event => event.preventDefault()}
            onClick={() => onSelect(item)}
            style={style}
            sx={{
                pl: 2,
                pr: 1,
                py: 0.5,
                cursor: "pointer",
                // Neutral, not the organisation's brand hover tint, which turns muddy under the violet chip.
                bgcolor: selected ? theme => alpha(theme.palette.text.primary, 0.09) : undefined,
            }}
        >
            <Box
                sx={{
                    flex: 1,
                    minWidth: 0,
                    ...(showLabel ? {overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis"} : {}),
                }}
            >
                <Box
                    component="span"
                    sx={{
                        ...variableChipSx,
                        // On the grey active row the violet tint would vanish; white keeps the pill's edge.
                        ...(selected ? {backgroundColor: "background.paper"} : {}),
                        display: "inline-block",
                        maxWidth: "100%",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        verticalAlign: "middle",
                    }}
                >
                    <HighlightedText text={item.chipText ?? item.key} query={query} emphasis="underline"/>
                </Box>

                {showLabel && (
                    <Typography
                        component="span"
                        variant="body2"
                        sx={{color: "text.secondary", ml: 1, verticalAlign: "middle"}}
                    >
                        <HighlightedText text={item.label} query={query}/>
                    </Typography>
                )}
            </Box>

            <Box
                component="span"
                role="button"
                aria-label={t("About this variable")}
                onMouseDown={event => event.preventDefault()}
                onMouseEnter={event => onOpenInfo(event.currentTarget, item, false)}
                onMouseLeave={onCloseInfoSoon}
                onClick={event => {
                    event.stopPropagation();
                    onOpenInfo(event.currentTarget, item, true);
                }}
                sx={{
                    display: "inline-flex",
                    flexShrink: 0,
                    p: 0.5,
                    borderRadius: 1,
                    cursor: "pointer",
                    color: infoOpen
                        ? "text.primary"
                        : selected ? "text.secondary" : "text.disabled",
                    "&:hover": {color: "text.primary"},
                }}
            >
                <InfoOutlinedIcon fontSize="small"/>
            </Box>
        </Stack>
    );
}

/** One variable row: chip, the label when it matched, and the ⓘ that opens the card. */
export const PickerRow = forwardRef(PickerRowInner) as <I extends PickerItem = PickerItem>(
    props: PickerRowProps<I> & {ref?: Ref<HTMLLIElement>},
) => ReactElement | null;

export default PickerRow;
